import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CalendarCheck, ShieldCheck, Clock, Mail, MessageCircle, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { api } from '../services/api';
import { dateFromKey } from '../utils/clinicTime';
import { Specialty } from '../types';
import { Footer, V2, SpecialtyIcon, contactLink, ADDRESS, CONTACT_EMAIL, WHATSAPP_DISPLAY, WHATSAPP_URL } from './shared';

/** "Psicología, fonoaudiología y psicopedagogía, en un mismo lugar." built from the specialties actually loaded. */
const headline = (list: Specialty[] | null): string => {
  const names = (list || []).map(s => s.name.trim().toLowerCase()).filter(Boolean);
  if (names.length < 2) return 'Atención profesional de la salud, en un mismo lugar.';
  const shown = names.length > 5 ? [...names.slice(0, 4), 'más'] : names;
  const text = shown.length > 1 ? `${shown.slice(0, -1).join(', ')} y ${shown[shown.length - 1]}` : shown[0];
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}, en un mismo lugar.`;
};

const HomeV2: React.FC = () => {
  const [specialties, setSpecialties] = useState<Specialty[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [nextSlots, setNextSlots] = useState<Array<{ specialtyId: number; specialtyName: string; date: string; time: string }> | null>(null);
  const { hash } = useLocation();

  useEffect(() => {
    api.get('/public/specialties').then(r => setSpecialties(r.data)).catch(() => setFailed(true));
    // real availability for the teaser card; if there is none (or the call fails) the card simply does not appear
    api.get('/public/next-slots', { params: { limit: 3 } }).then(r => setNextSlots(r.data.slots)).catch(() => setNextSlots([]));
  }, []);

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [hash, specialties]);

  return (
    <V2>
      
      <main>
        <section className="v2-hero">
          <div className={`wrap grid ${nextSlots && nextSlots.length > 0 ? '' : 'solo'}`}>
            <div>
              <h1>Tu salud, en manos <em>cercanas</em>.</h1>
              <p className="lead">{headline(specialties)} Elegí tu especialidad, pedí tu turno en un minuto y te confirmamos por mail.</p>
              <div className="actions">
                <a href="#especialidades" className="btn2 btn2-cta">Pedir un turno <ArrowRight size={18} /></a>
                <Link to="/login" className="btn2 btn2-ghost">Ingresar</Link>
              </div>
              <div className="v2-trust">
                <span><ShieldCheck size={18} /> Profesionales matriculados</span>
                <span><Clock size={18} /> Respuesta en menos de 24 h</span>
                <span><CalendarCheck size={18} /> Sin registrarte</span>
              </div>
            </div>
            {nextSlots && nextSlots.length > 0 && (
              <aside className="v2-card-float" aria-label="Próximos horarios disponibles">
                <h3>Próximos horarios libres</h3>
                <p className="sub">Elegí uno y reservalo en un minuto</p>
                {nextSlots.map(x => (
                  <Link key={x.specialtyId} to={`/reservar/${x.specialtyId}`} className="v2-mini-slot">
                    {format(dateFromKey(x.date), 'EEE d', { locale: es }).replace(/^./, c => c.toUpperCase())} · {x.time}
                    <span>{x.specialtyName}</span>
                  </Link>
                ))}
              </aside>
            )}
          </div>
        </section>

        <section className="v2-section" id="especialidades">
          <div className="wrap">
            <div className="v2-eyebrow">Especialidades</div>
            <h2>¿Con quién querés atenderte?</h2>
            <p className="intro">Elegí una especialidad y te mostramos los días y horarios disponibles.</p>

            {failed && <div className="empty" style={{ marginTop: 32 }}>No pudimos cargar las especialidades. <button className="btn2 btn2-ghost btn2-sm" onClick={() => window.location.reload()}>Reintentar</button></div>}
            <div className="v2-grid">
              {!specialties && !failed && Array.from({ length: 6 }).map((_, i) => <div className="skel" key={i} />)}
              {specialties?.map(s => (
                <Link key={s.id} to={`/reservar/${s.id}`} className="v2-spec" aria-label={`Pedir turno de ${s.name}`}>
                  <div className="ic"><SpecialtyIcon name={s.icon} /></div>
                  <div className="txt">
                    <h3>{s.name}</h3>
                    <p>{s.description}</p>
                  </div>
                  <div className="foot">
                    <span>Pedir turno →</span>
                    <span className="n">{s.professional_count} {s.professional_count === 1 ? 'profesional' : 'profesionales'}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="v2-section alt" id="como-funciona">
          <div className="wrap">
            <div className="v2-eyebrow">Cómo funciona</div>
            <h2>Tres pasos, sin vueltas</h2>
            <div className="v2-steps">
              <div className="v2-step"><h3>Elegí día y horario</h3><p>Ves solo los horarios realmente disponibles. Podés elegir profesional o dejar que lo asignemos.</p></div>
              <div className="v2-step"><h3>Dejanos tus datos</h3><p>Nombre, mail y un teléfono. Si ya tenés cuenta, completamos todo por vos.</p></div>
              <div className="v2-step"><h3>Te confirmamos</h3><p>Recibís la confirmación por mail y un recordatorio el día anterior.</p></div>
            </div>
          </div>
        </section>

        <section className="v2-section" id="contacto">
          <div className="wrap">
            <div className="v2-eyebrow">Contacto</div>
            <h2>Dónde encontrarnos</h2>
            <p className="intro">¿Dudas antes de pedir turno? Escribinos y te ayudamos a elegir la especialidad correcta.</p>
            <div className="v2-contact">
              <ul className="v2-contact-list">
                <li><MapPin size={20} aria-hidden="true" /><div><b>Dirección</b><a href={ADDRESS.map} target="_blank" rel="noreferrer">{ADDRESS.line}, {ADDRESS.area}</a></div></li>
                <li><Clock size={20} aria-hidden="true" /><div><b>Horario</b><span>Lunes a sábados, con turno previo</span></div></li>
                <li><MessageCircle size={20} aria-hidden="true" /><div><b>WhatsApp</b><a href={WHATSAPP_URL} target="_blank" rel="noreferrer">{WHATSAPP_DISPLAY}</a></div></li>
                <li><Mail size={20} aria-hidden="true" /><div><b>Email</b><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></div></li>
                <li className="v2-contact-actions">
                  <a className="btn2 btn2-cta" href={contactLink.href} {...(contactLink.external ? { target: '_blank', rel: 'noreferrer' } : {})}>{contactLink.external ? <MessageCircle size={18} /> : <Mail size={18} />} Escribinos por {contactLink.label}</a>
                  <a className="btn2 btn2-ghost" href={ADDRESS.map} target="_blank" rel="noreferrer">Cómo llegar</a>
                </li>
              </ul>
              <div className="v2-map">
                <iframe title="Mapa de Consultorios Integrales Urquiza" src={ADDRESS.embed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer specialties={specialties} />
    </V2>
  );
};

export default HomeV2;
